import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import { useTranslation } from 'react-i18next'

import { adminApi } from '../../api/endpoints'
import { useFormatters } from '../../hooks/useFormatters'
import { ErrorState, EmptyState } from '../../components/common/States'
import type { BookingStatus } from '../../types/api'

const STATUSES: BookingStatus[] = ['PENDING', 'CONFIRMED', 'CANCELLED', 'COMPLETED']

const STATUS_COLOR: Record<BookingStatus, 'warning' | 'success' | 'error' | 'default'> = {
  PENDING: 'warning',
  CONFIRMED: 'success',
  CANCELLED: 'error',
  COMPLETED: 'default',
}

export function AdminBookingsPage() {
  const { t } = useTranslation()
  const { dateShort, moneyPrecise } = useFormatters()
  const queryClient = useQueryClient()

  const [status, setStatus] = useState<BookingStatus | undefined>('PENDING')
  const [page, setPage] = useState(0)
  const size = 20

  const bookings = useQuery({
    queryKey: ['admin', 'bookings', status, page],
    queryFn: () => adminApi.bookings({ status, page, size }),
  })

  const updateStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: BookingStatus }) =>
      adminApi.updateBookingStatus(id, next),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] })
      // Confirming or cancelling changes the calendar too.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'calendar'] })
    },
  })

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 3 }}>
        {t('admin.bookings')}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          label={t('common.all')}
          onClick={() => {
            setStatus(undefined)
            setPage(0)
          }}
          color={status === undefined ? 'primary' : 'default'}
          variant={status === undefined ? 'filled' : 'outlined'}
        />
        {STATUSES.map((option) => (
          <Chip
            key={option}
            label={t(`booking.status${option}`)}
            onClick={() => {
              setStatus(option)
              setPage(0)
            }}
            color={status === option ? 'primary' : 'default'}
            variant={status === option ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>

      {bookings.isError && <ErrorState error={bookings.error} onRetry={() => void bookings.refetch()} />}

      {bookings.data?.content.length === 0 && (
        <EmptyState icon="📭" title={t('admin.noPending')} />
      )}

      {bookings.data && bookings.data.content.length > 0 && (
        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('booking.reference')}</TableCell>
                  <TableCell>{t('admin.units')}</TableCell>
                  <TableCell>{t('booking.yourStay')}</TableCell>
                  <TableCell>{t('booking.name')}</TableCell>
                  <TableCell align="right">{t('booking.total')}</TableCell>
                  <TableCell>{t('admin.dashboard')}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.data.content.map((booking) => (
                  <TableRow key={booking.id} hover>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {booking.reference}
                    </TableCell>
                    <TableCell>{booking.unitName}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {dateShort(booking.checkIn)} → {dateShort(booking.checkOut)}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('common.night', { count: booking.nights })} ·{' '}
                        {t('common.guest', { count: booking.adults + booking.children })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {booking.guestName}
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        <Link href={`mailto:${booking.guestEmail}`}>{booking.guestEmail}</Link>
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {moneyPrecise(booking.totalAmount)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={STATUS_COLOR[booking.status]}
                        label={t(`booking.status${booking.status}`)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {booking.status === 'PENDING' && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={updateStatus.isPending}
                            onClick={() => updateStatus.mutate({ id: booking.id, next: 'CONFIRMED' })}
                          >
                            {t('admin.confirm')}
                          </Button>
                        )}
                        {(booking.status === 'PENDING' || booking.status === 'CONFIRMED') && (
                          <Button
                            size="small"
                            color="error"
                            disabled={updateStatus.isPending}
                            onClick={() => updateStatus.mutate({ id: booking.id, next: 'CANCELLED' })}
                          >
                            {t('admin.cancelBooking')}
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={bookings.data.totalElements}
            page={page}
            rowsPerPage={size}
            rowsPerPageOptions={[size]}
            onPageChange={(_, next) => setPage(next)}
          />
        </Paper>
      )}

      {updateStatus.isError && <ErrorState error={updateStatus.error} />}
    </Box>
  )
}
